/**
 * WSBFWordmark — hand-crafted inline SVG lettering for the WSTF homepage.
 *
 * Design intent:
 *  - Ultra-bold EXTENDED italic letterforms (Archivo var: wdth 125, wght 900, italic 1)
 *  - ~12° forward slant via SVG skewX
 *  - Metallic gold gradient (shared tokens):
 *      #FFE066 → #FFD700 → #E5B31E → #8A5A00
 *  - Angular diagonal CUT notches drawn through the strokes
 *  - Subtle outer glow
 *
 * Props:
 *   size: "sm" (navbar), "md", "lg", "xl" (hero)
 *   lockup: when true, show the spaced-out "WORLD SPORTS TRADING FEDERATION" line beneath
 */
export function WSBFWordmark({ size = "md", lockup = false, className = "", lockupClassName = "" }) {
  const heights = { sm: 24, md: 40, lg: 72, xl: 108 };
  const h = heights[size] || heights.md;
  // Reserve viewBox room for the skewed letters so the W doesn't get clipped.
  const vbW = 440;
  const vbH = 104;
  const w = Math.round(h * (vbW / vbH));

  // Unique IDs so multiple instances (nav + hero) don't collide on <defs>.
  const uid = `wsbf-${size}`;

  return (
    <div
      className={`inline-flex flex-col items-start leading-none ${className}`}
      data-testid={`wsbf-wordmark-${size}`}
      aria-label="WSTF — World Sports Trading Federation"
    >
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        width={w}
        height={h}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        style={{ display: "block" }}
      >
        <defs>
          {/* Polished gold vertical gradient — matches --wsbf-grad-gold tokens */}
          <linearGradient id={`${uid}-grad`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%"  stopColor="#FFE066" />
            <stop offset="20%" stopColor="#FFD700" />
            <stop offset="55%" stopColor="#E5B31E" />
            <stop offset="100%" stopColor="#8A5A00" />
          </linearGradient>

          {/* Subtle outer glow */}
          <filter id={`${uid}-glow`} x="-8%" y="-25%" width="116%" height="150%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Mask of just the lettering, used to clip the diagonal cut notches
              so they only slice ACROSS the gold strokes. */}
          <mask id={`${uid}-mask`} maskUnits="userSpaceOnUse" x="0" y="0" width={vbW} height={vbH}>
            <rect x="0" y="0" width={vbW} height={vbH} fill="black" />
            <g transform="skewX(-12)">
              <text
                x="22"
                y="90"
                fill="white"
                fontFamily='"Archivo", "Arial Black", "Barlow Condensed", sans-serif'
                fontWeight="900"
                fontStyle="italic"
                fontSize="112"
                letterSpacing="3"
                style={{ fontVariationSettings: '"wdth" 125, "wght" 900' }}
              >
                WSTF
              </text>
            </g>
          </mask>
        </defs>

        {/* Gold lettering */}
        <g filter={`url(#${uid}-glow)`}>
          <g transform="skewX(-12)">
            <text
              x="22"
              y="90"
              fill={`url(#${uid}-grad)`}
              fontFamily='"Archivo", "Arial Black", "Barlow Condensed", sans-serif'
              fontWeight="900"
              fontStyle="italic"
              fontSize="112"
              letterSpacing="3"
              style={{ fontVariationSettings: '"wdth" 125, "wght" 900' }}
            >
              WSTF
            </text>
          </g>

          {/* Angular diagonal CUT notches — only visible where the lettering is */}
          <g mask={`url(#${uid}-mask)`}>
            <rect x="-40" y="68" width="520" height="5" transform="rotate(-18 220 70)" fill="#000" opacity="0.98" />
            <rect x="-40" y="40" width="520" height="3" transform="rotate(-18 220 41)" fill="#000" opacity="0.70" />
            <rect x="-40" y="72.5" width="520" height="0.8" transform="rotate(-18 220 73)" fill="#FFE066" opacity="0.55" />
          </g>
        </g>
      </svg>

      {lockup && (
        <div
          className={`wsbf-gold-text uppercase font-heading mt-1 pl-0.5 ${lockupClassName}`}
          style={{
            fontSize: size === "sm" ? "8px" : size === "xl" ? "13px" : "10px",
            letterSpacing: "0.42em",
            fontStyle: "normal",
            fontVariationSettings: '"wdth" 110, "wght" 700',
          }}
        >
          World Sports Trading Federation
        </div>
      )}
    </div>
  );
}

export default WSBFWordmark;
